import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import * as Styles from './AiBuilderCreateSite.styles';
import { CREATE_SITE_TABS } from './AiBuilderCreateSite.enum';
import { IconButton } from 'gatorcomponents';
import { NameStep } from './components/NameStep';
import { SegmentStep } from './components/SegmentStep';
import { InfosStep } from './components/InfosStep';
import imageStepName from '@/media/onboarding/aiBuilder/step-name.png';
import imageStepNameEs from '@/media/onboarding/aiBuilder/step-name-es.png';
import imageStepSegment from '@/media/onboarding/aiBuilder/step-segment.png';
import imageStepSegmentEs from '@/media/onboarding/aiBuilder/step-segment-es.png';
import imageStepInfo from '@/media/onboarding/aiBuilder/step-info.png';
import imageStepInfoEs from '@/media/onboarding/aiBuilder/step-info-es.png';
import starImage from '@/media/onboarding/aiBuilder/star-to-background.png';
import iconCircleCheck from '@/media/onboarding/aiBuilder/bg-circle-check.svg';
import iconMaia from '@/media/onboarding/aiBuilder/AiAnimation.webp';
import { useResize } from '@/hooks/useResize';
import { isBRServer } from '@/utils/Validators/validation';
import { LoadingContent } from '../LoadingScreen';

const AiBuilderCreateSite = ({
  locales,
  onContinue,
  trackingEventsByStep,
  createSiteState,
  builderProvisioningLoading,
  redirectSuccessPageAiBuilder,
  onGoBack,
  userCountry,
}) => {
  const [currentTab, setCurrentTab] = useState(CREATE_SITE_TABS.NAME);
  const [displayedTab, setDisplayedTab] = useState(currentTab);
  const [fadeState, setFadeState] = useState('fade-in');
  const [nameInputValue, setNameInputValue] = useState('');
  const [segmentInputValue, setSegmentInputValue] = useState('');
  const [description, setDescription] = useState('');
  const [telephoneInputValue, setTelephoneInputValue] = useState('');
  const [emailInputValue, setEmailValue] = useState('');
  const [tabsCompleted, setTabsCompleted] = useState([]);
  const [widthLineTabs, setWidthLineTabs] = useState({
    [CREATE_SITE_TABS.NAME]: {
      width: 0,
    },
    [CREATE_SITE_TABS.SEGMENT]: {
      width: 0,
    },
    [CREATE_SITE_TABS.INFOS]: {
      width: 0,
    },
  });

  const tabRefs = useRef([]);
  const tabIndexRefs = useRef([]);
  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 600;

  const segmentFormatted = useMemo(() => {
    const options = locales.steps[CREATE_SITE_TABS.SEGMENT].optionsList || [];

    const optionFound = options.find(option => option.label && option.label.toLowerCase() === segmentInputValue.toLowerCase());

    if (optionFound && optionFound.value) {
      return optionFound.value;
    }

    return segmentInputValue;
  }, [segmentInputValue, locales]);

  const projectData = useMemo(() => ({
    company: nameInputValue,
    segment: segmentFormatted,
    description,
    phone: telephoneInputValue,
    email: emailInputValue,
    language: isBRServer ? 'pt-br' : 'es',
  }), [nameInputValue, description, telephoneInputValue, emailInputValue, segmentFormatted]);

  const handleEventsByStep = useCallback((step) => {
    if (trackingEventsByStep && step in trackingEventsByStep) {
      trackingEventsByStep[step]();
    }
  }, [trackingEventsByStep]);

  const handleNameStepContinue = useCallback(() => {
    handleEventsByStep(currentTab);
    setCurrentTab(CREATE_SITE_TABS.SEGMENT);
    setTabsCompleted([...tabsCompleted, CREATE_SITE_TABS.NAME]);
  }, [handleEventsByStep, currentTab, tabsCompleted]);

  const handleSegmentStepContinue = useCallback(() => {
    handleEventsByStep(currentTab);
    setCurrentTab(CREATE_SITE_TABS.INFOS);
    setTabsCompleted([...tabsCompleted, CREATE_SITE_TABS.SEGMENT]);
  }, [handleEventsByStep, currentTab, tabsCompleted]);

  const handleCreateSite = useCallback(() => {
    handleEventsByStep(currentTab);
    onContinue && onContinue({ projectData });
  }, [onContinue, projectData, handleEventsByStep, currentTab]);

  const nameStepProps = useCallback(() => ({
    continueButtonClick: handleNameStepContinue,
    nameStepLocales: locales.steps[CREATE_SITE_TABS.NAME],
    nameInputValue,
    setNameInputValue,
    mobile,
    iconMaia,
  }), [handleNameStepContinue, locales, nameInputValue, mobile]);

  const segmentStepProps = useCallback(() => ({
    continueButtonClick: handleSegmentStepContinue,
    segmentStepLocales: locales.steps[CREATE_SITE_TABS.SEGMENT],
    segmentInputValue,
    setSegmentInputValue,
    mobile,
    iconMaia,
  }), [handleSegmentStepContinue, locales, segmentInputValue, mobile]);

  const infosStepProps = useCallback(() => ({
    continueButtonClick: handleCreateSite,
    infosStepLocales: locales.steps[CREATE_SITE_TABS.INFOS],
    description,
    userCountry,
    setDescription,
    telephoneInputValue,
    setTelephoneInputValue,
    emailInputValue,
    setEmailValue,
    mobile,
    iconMaia,
  }), [locales, description, telephoneInputValue, emailInputValue, handleCreateSite, mobile, userCountry]);

  const tabContent = {
    [CREATE_SITE_TABS.NAME]: <NameStep {...nameStepProps()} />,
    [CREATE_SITE_TABS.SEGMENT]: <SegmentStep {...segmentStepProps()} />,
    [CREATE_SITE_TABS.INFOS]: <InfosStep {...infosStepProps()} />,
  };

  const getImagesByStep = {
    [CREATE_SITE_TABS.NAME]: isBRServer ? imageStepName : imageStepNameEs,
    [CREATE_SITE_TABS.SEGMENT]: isBRServer ? imageStepSegment : imageStepSegmentEs,
    [CREATE_SITE_TABS.INFOS]: isBRServer ? imageStepInfo : imageStepInfoEs,
  };

  const tabsOptions = useMemo(() => ([
    {
      id: CREATE_SITE_TABS.NAME,
      index: '1',
      label: locales.steps[CREATE_SITE_TABS.NAME].label,
    },
    {
      id: CREATE_SITE_TABS.SEGMENT,
      index: '2',
      label: locales.steps[CREATE_SITE_TABS.SEGMENT].label,
    },
    {
      id: CREATE_SITE_TABS.INFOS,
      index: '3',
      label: locales.steps[CREATE_SITE_TABS.INFOS].label,
    },
  ]), [locales]);

  const calculateTabLine = useCallback(() => {
    if (tabIndexRefs.current[0] && tabIndexRefs.current[1] && tabIndexRefs.current[2]) {
      const tabName = tabIndexRefs.current[0].getBoundingClientRect();
      const tabSegment = tabIndexRefs.current[1].getBoundingClientRect();
      const tabInfo = tabIndexRefs.current[2].getBoundingClientRect();

      setWidthLineTabs({
        [CREATE_SITE_TABS.NAME]: {
          width: (Math.abs(tabSegment.left - tabName.left)) - 24,
        },
        [CREATE_SITE_TABS.SEGMENT]: {
          width: (Math.abs(tabInfo.left - tabSegment.left)) - 24,
        },
        [CREATE_SITE_TABS.INFOS]: {
          width: 0,
        },
      });
    }
  }, [setWidthLineTabs]);

  useLayoutEffect(() => {
    calculateTabLine();
  }, [calculateTabLine, tabsOptions]);

  useEffect(() => {
    window.addEventListener('resize', calculateTabLine);
    return () => window.removeEventListener('resize', calculateTabLine);
  }, [calculateTabLine]);

  const controlTabFade = useCallback(() => {
    if (currentTab !== displayedTab) {
      setFadeState('fade-out');

      const timeout = setTimeout(() => {
        setDisplayedTab(currentTab);
        setFadeState('fade-in');
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [currentTab, displayedTab]);

  useEffect(() => {
    controlTabFade();
  }, [controlTabFade]);

  const handleCloseOnSuccess = useCallback(() => {
    if (createSiteState.status === 'success') {
      redirectSuccessPageAiBuilder && redirectSuccessPageAiBuilder();
    }
  }, [createSiteState.status, redirectSuccessPageAiBuilder]);

  useEffect(() => {
    handleCloseOnSuccess();
  }, [handleCloseOnSuccess]);

  const handleGoBack = () => {
    if (currentTab === CREATE_SITE_TABS.NAME) {
      onGoBack && onGoBack();
      return;
    }

    if (currentTab === CREATE_SITE_TABS.SEGMENT) {
      setCurrentTab(CREATE_SITE_TABS.NAME);
      setDisplayedTab(CREATE_SITE_TABS.NAME);
      setTabsCompleted(prev => prev.filter(tab => tab !== CREATE_SITE_TABS.NAME));
      return;
    }

    if (currentTab === CREATE_SITE_TABS.INFOS) {
      setCurrentTab(CREATE_SITE_TABS.SEGMENT);
      setDisplayedTab(CREATE_SITE_TABS.SEGMENT);
      setTabsCompleted(prev => prev.filter(tab => tab !== CREATE_SITE_TABS.SEGMENT));
    }
  };

  if (builderProvisioningLoading || (createSiteState && createSiteState.loading)) {
    return <LoadingContent locales={locales} />;
  }

  return (
    <Styles.Wrapper data-testid="ai-builder-create-site">
      <Styles.ButtonBackWrapper>
        <IconButton icon="IconArrow" onClick={handleGoBack} size="sm" color="lowDark" />
      </Styles.ButtonBackWrapper>

      <Styles.PromoPanel>
        <Styles.PromoPanelInner>
          <Styles.PromoTitleContainer>
            <Styles.PromoTitle>
              {locales.steps[currentTab].promoBannerTitle}
            </Styles.PromoTitle>
          </Styles.PromoTitleContainer>

          <Styles.PromoImage src={getImagesByStep[currentTab]} alt="promo banner" />
        </Styles.PromoPanelInner>
        <Styles.StarImage src={starImage} alt="loading-star" />
      </Styles.PromoPanel>

      <Styles.FormContainer>
        <Styles.TabsWrapper>
          {tabsOptions.map((tab, index) => (
            <Styles.TabItem
              key={tab.id}
              $active={currentTab === tab.id}
              ref={(element) => { tabRefs.current[index] = element; }}
            >
              <Styles.TabIndex ref={(el) => { tabIndexRefs.current[index] = el; }} $active={currentTab === tab.id} $completed={tabsCompleted.includes(tab.id)}>
                {tabsCompleted.includes(tab.id) ? (
                  <Styles.IconCircleCheck src={iconCircleCheck} />
                ) : tab.index }
              </Styles.TabIndex>
              <Styles.TabLabel $active={currentTab === tab.id}>{tab.label}</Styles.TabLabel>
              <Styles.TabLine
                $active={tabsCompleted.includes(tab.id)}
                $isLastTab={index === tabsOptions.length - 1}
                style={{ width: widthLineTabs[tab.id].width }}
              />
            </Styles.TabItem>
          ))}
        </Styles.TabsWrapper>

        <Styles.Content>
          <Styles.FadeContent $fadeState={fadeState}>
            {tabContent[displayedTab]}
          </Styles.FadeContent>
        </Styles.Content>
      </Styles.FormContainer>
    </Styles.Wrapper>
  );
};

export default AiBuilderCreateSite;
